<template>
  <main class="report-warehouse-product-balances">
    <h1 class="report-warehouse-product-balances__header">
      Склад: остатки товара
    </h1>
    <form
      class="report-warehouse-product-balances__filters"
      v-if="hasFetchedOptions"
      v-on:submit.prevent
    >
      <date-range-with-presets
        v-bind:presets="presetsOfDates"
        v-bind:value="selectedDates"
        custom-color-date="black"
        v-on:input="onInputDates"
      />
      <input-text-search
        v-model="match"
        placeholder="Наименование"
        :violations="matchViolations"
        v-on:input="debounceFetchReport"
      />
    </form>
    <div class="report-warehouse-product-balances__row">
      <input-checkbox
        v-model="isShowLowStockOnly"
        title="С критическим остатком"
        v-on:input="debounceFetchReport"
      />
      <button
        type="button"
        v-on:click="fetchReportSpreadsheet"
        class="report-warehouse-product-balances__button">EXCEL</button>
    </div>
    <div class="report-warehouse-product-balances__body">
      <table
        class="report-warehouse-product-balances-table report-warehouse-product-balances__table"
      >
        <thead class="report-warehouse-product-balances-table__head-line">
          <tr>
            <th
              class="report-warehouse-product-balances-table__head-cell"
              :class="field"
              v-bind:key="field"
              v-for="({ field, title, sorts }) in reportColumns"
            >
              <sort-by-column
                class="report-warehouse-product-balances-table__sort"
                v-bind:items="sorts"
                v-bind:label="title"
                v-bind:value="sort"
                v-if="sorts.length"
                v-on:input="onInputSort" />
              <template v-else>{{ title }}</template>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            class="report-warehouse-product-balances-table__body-line"
            v-bind:key="`table-rows:${rowIndex}`"
            v-for="(row, rowIndex) in reportRows"
          >
            <td
              class="report-warehouse-product-balances-table__body-cell"
              v-bind:key="`table-rows:${rowIndex}:cells:${cellIndex}`"
              v-for="(title, cellIndex) in row"
            >
              <span>{{ title }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </main>
</template>

<script>
import DateRangeWithPresets from '@/components/DateRangeWithPresets.vue';
import InputTextSearch from '@/components/InputTextSearch.vue';
import debounce from 'lodash.debounce';
import saveFileMixin from '@/mixins/saveFileMixin';
import { getISODateStringByDate } from '@/mixins/dateMixin';
import notifierMixin from '@/mixins/notifierMixin';
import InputCheckbox from '@/components/InputCheckbox.vue';

/**
 * @description Компонент сортировки по столбцу.
 * @type {Vue}
 */
const SortByColumn = {
  computed: {
    headTitle() {
      const head = this.sortedItems[0];
      return head ? head.title : '';
    },
    isNotSelected() {
      return this.items.some(({ value }) => value === this.value) !== true;
    },
    sortedItems() {
      const items = [
        {
          checked: this.isNotSelected,
          title: 'Без сортировки',
          value: '',
        },
        ...this.items.map(({ title, value }) => ({
          checked: value === this.value,
          title,
          value,
        })),
      ];
      let slicedPosition = 0;
      const checkedIndex = items.findIndex(({ checked }) => checked);
      if (checkedIndex !== -1) {
        slicedPosition = checkedIndex + 1;
      }
      if (slicedPosition === items.length) {
        return items;
      }
      return [
        ...items.slice(slicedPosition),
        ...items.slice(0, slicedPosition),
      ];
    },
  },
  render(createElement) {
    const name = `report-sort-${this.title}`;
    return createElement('label', { attrs: { title: this.headTitle }, class: !this.isNotSelected ? 'selected' : '' }, [
      createElement('span', this.label).children[0],
      ...this.sortedItems.map(({ checked, title, value }) => createElement('input', {
        attrs: {
          'aria-label': title,
          checked,
          name,
          title,
          type: 'radio',
        },
        domProps: { checked },
        key: `column:${this.label}:sort:${value}`,
        on: { input: () => { this.$emit('input', value); } },
      })),
    ]);
  },
  props: {
    items: {
      required: true,
      type: Array,
    },
    label: {
      required: true,
      type: String,
    },
    value: {
      required: true,
      type: String,
    },
  },
};

/**
 * Часть названия отчета: Отчет по остатку товаров
 *
 * @type {string}
 */
const PART_OF_REPORT_REPORT_BY_REMAINDER_ITEMS = 'Отчет по остатку товаров';

/**
 * Часть названия отчета: (с критическим остатком)
 *
 * @type {string}
 */
const PART_OF_REPORT_WITH_CRITICAL_REMAINDER = '(с критическим остатком)';

/**
 * @description Компонент отчета по планам лечения.
 * @type {Vue}
 */
export default {
  components: {
    DateRangeWithPresets,
    InputTextSearch,
    SortByColumn,
    InputCheckbox,
  },
  computed: {
    /**
     * @description Набор предварительных дат для фильтрации.
     * @type {object[]}
     */
    presetsOfDates() {
      return this
        .periodOptions
        .map(({ from_to: [from, to], title, value }) => {
          const periodDates = [new Date(from), new Date(to)];
          return {
            id: value,
            label: title,
            title,
            toValue: () => periodDates,
          };
        });
    },
  },
  created() {
    this
      .fetchOptions()
      .then(() => {
        this
          .presetsOfDates
          .slice(2, 3)
          .forEach((candidate) => {
            this.selectedDates = candidate.toValue();
          });
        this.fetchReport();
      })
      .finally(() => { this.$nextTick(() => { this.hasFetchedOptions = true; }); });
  },
  data() {
    return {
      /** Наименование филиала */
      branchName: '',
      isFinishedReport: false,
      isShowLowStockOnly: false,
      hasFetchedOptions: false,
      periodOptions: [],
      reportColumns: [],
      reportId: null,
      reportRows: [],
      sort: '',
      selectedDates: [],
      match: '',
      matchViolations: [],
    };
  },
  methods: {
    debounceFetchReport: debounce(function _() { this.fetchReport(); }, 1000),
    /**
     * @description Получить опции отчета.
     * @returns {Promise}
     */
    fetchOptions() {
      return this
        .$reportGateway
        .getWarehouseProductBalancesReportOptions()
        .then((result) => {
          result.forEach(({ period_types: periods }) => {
            this.periodOptions = periods;
          });
        });
    },
    /**
     * @description Получить данные отчета.
     * @returns {void}
     */
    fetchReport() {
      this.matchViolations = [];
      if (this.match && this.match.length < 3) {
        this.matchViolations.push('Необходимо ввести не менее 3-х символов');
        return;
      }
      this.isFinishedReport = false;
      const [dateFrom, dateTo] = this.selectedDates.map(getISODateStringByDate);
      this
        .$reportGateway
        .getWarehouseProductBalancesReport(
          dateFrom,
          dateTo,
          this.sort,
          this.match,
          this.isShowLowStockOnly,
        )
        .then((result) => {
          result.forEach(({
            columns, report_id: reportId, rows, branch_name: branchName,
          }) => {
            this.reportColumns = columns;
            this.reportId = reportId;
            this.reportRows = rows;
            this.branchName = branchName;
          });
        })
        .finally(() => { this.isFinishedReport = true; });
    },
    /**
     * @description Получить отчет в виде таблицы.
     * @returns {void}
     */
    fetchReportSpreadsheet() {
      const reportName = this.getReportFileName();
      const onSuccess = (response) => this
        .saveFile(response, reportName);
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      this
        .$reportGateway
        .getWarehouseProductBalancesReportBySheet(this.reportId)
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Сформировать название отчета для файла.
     * @returns {string}
     */
    getReportFileName() {
      const parts = [];
      parts.push(PART_OF_REPORT_REPORT_BY_REMAINDER_ITEMS);
      if (this.isShowLowStockOnly) {
        parts.push(PART_OF_REPORT_WITH_CRITICAL_REMAINDER);
      }
      if (this.branchName && this.branchName.trim() !== '') {
        parts.push(this.branchName.trim());
      }
      const [dateFrom, dateTo] = this.selectedDates.map(getISODateStringByDate);
      parts.push(`${dateFrom} — ${dateTo}`);
      const fileName = parts
        .filter((part) => part && part.trim() !== '')
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
      return `${fileName}.xls`;
    },
    /**
     * @description Обработать выбор периода для отчета.
     * @param {Date[]} value
     * @returns {void}
     */
    onInputDates(value) {
      if (value.length === 2) {
        this.selectedDates = value.sort(
          (first, second) => Number(first.getTime() > second.getTime()),
        );
        this.debounceFetchReport();
      } else {
        this.selectedDates = value;
      }
    },
    /**
     * @description Обработать ввод сортировки.
     * @param {string} value
     * @returns {void}
     */
    onInputSort(value) {
      this.sort = value;
      this.debounceFetchReport();
    },
    onInputShowLowStockOnly() {},
  },
  mixins: [notifierMixin, saveFileMixin],
  name: 'ClinicPanelReportWarehouseProductBalances',
};
</script>

<style lang="scss">
@use "@/scss/buttons";
.report-warehouse-product-balances {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  overflow: scroll;
  padding: 2rem 1rem;
  width: 100%;
  &__filters {
    align-items: baseline;
    display: flex;
    flex-direction: row;
    gap: 1rem;
    width: 100%;
  }
  &__row {
    display: flex;
    flex-direction: row;
    align-items: center;
  }
  &-table {
    border-collapse: collapse;
    font-size: .875rem;
    line-height: 1rem;
    overflow: auto;
    table-layout: fixed;
    width: 100%;
    &__body-cell {
      padding: 15px 8px;
      text-align: left;
      text-transform: none;
      white-space: normal;
    }
    &__body-line {
      border-bottom: 1px solid var(--fill-color);
    }
    &__head-cell {
      padding: 10px 8px;
      text-align: left;
      &.number {
        width: 40px;
      }
      &.name {
        width: 20%;
      }
      &.category {
        width: 16%;
      }
    }
    &__head-line {
      border-bottom: 2px solid var(--action-color);
      font-weight: 700;
    }
    &__sort {
      cursor: pointer;
      input {
        display: none;
      }
      &::after {
        background-image: url("@/assets/img/checked/checked-grey.svg");
        background-position: center;
        background-repeat: no-repeat;
        background-size: contain;
        content: '';
        display: inline-block;
        height: 8px;
        margin: 0 0 0 2px;
        min-height: 8px;
        min-width: 8px;
        width: 8px;
      }
      &.selected {
        color: var(--action-color);
      }
    }
  }
  &__button {
    @include buttons.btn-transparent(155px, 48px, 16px);
    margin: 10px 0 10px auto;
    flex-shrink: 0;
    &:before {
      content: "";
      background-image: url("@/assets/img/exel.svg");
      background-repeat: no-repeat;
      background-size: contain;
      width: 16px;
      height: 16px;
      margin: 0 8px 0 0;
    }
  }
}
@media screen and (max-width: 1360px) {
  .report-warehouse-product-balances {
    &__body {
      overflow: scroll;
    }
    &-table {
      width: 1360px;
    }
  }
}
@media screen and (max-width: 1260px) {
  .report-warehouse-product-balances {
    &__filters {
      flex-direction: column;
    }
  }
}
</style>
